import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { Header, Footer } from './components';
import { ToastProvider } from './components/common/Toast';
import { 
  HomePage, 
  OffersPage, 
  DemandsPage, 
  AuctionsPage, 
  PropertyDetailPage,
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
  NewsletterUnsubscribePage,
  NewListingPage,
  EditListingPage,
  TermsPage,
  PrivacyPage,
  PricingPage,
  AdminPage,
  BuyTokensPage,
  PaymentSuccessPage,
  PaymentFailedPage,
  MyListingsPage,
  ProfilePage,
  BlogPage,
  FAQPage,
  AboutPage,
  MapPage,
  FavoritesPage,
  SavedSearchesPage,
  CalendarPage,
  CompanyProfilePage,
  CompaniesListPage
} from './pages';
import NotificationsPage from './pages/NotificationsPage';
import { AIAssistant } from './components/ai';
import { useAppStore } from './store/appStore';
import { sessionUtils, getFavorites, refreshUserData, getNotifications } from './api/client';

// Handle SPA redirect from 404.html fallback
function SPARedirectHandler() {
  const navigate = useNavigate();
  
  useEffect(() => {
    const redirectPath = sessionStorage.getItem('spa_redirect_path');
    if (redirectPath && redirectPath !== '/') {
      sessionStorage.removeItem('spa_redirect_path');
      navigate(redirectPath, { replace: true });
    }
  }, [navigate]);
  
  return null;
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function App() {
  const { theme, setUser, setChecking, user, loadPropertiesFromApi, setFavorites, setTokenBalance, setNotifications } = useAppStore();

  // Restore session on mount - 24h persistence
  useEffect(() => {
    async function restoreSession() {
      // Proveri da li sesija postoji i nije istekla
      if (sessionUtils.isSessionValid()) {
        const savedUser = sessionUtils.getUser();
        if (savedUser) {
          // Set initial user from cache
          setUser({
            id: savedUser.id,
            email: savedUser.email,
            name: savedUser.name || '',
            emailVerified: savedUser.emailVerified,
            type: savedUser.userType || savedUser.type,
            role: savedUser.role || 'user',
            tokenBalance: savedUser.tokenBalance,
          });
          
          // Refresh session expiry (extend by another 24h on activity)
          sessionUtils.refreshSession();
          
          // Fetch fresh user data from API (tokens, etc.)
          try {
            const freshData = await refreshUserData();
            if (freshData.success && freshData.user) {
              // Update user with fresh data - role MUST come from server
              setUser({
                id: freshData.user.id,
                email: freshData.user.email,
                name: freshData.user.name || '',
                emailVerified: freshData.user.emailVerified,
                type: freshData.user.userType || freshData.user.type,
                role: freshData.user.role || 'user',
                tokenBalance: freshData.tokenBalance || 0,
              });
              // Also update token balance in store
              if (freshData.tokenBalance !== undefined) {
                setTokenBalance(freshData.tokenBalance);
              }
            }
          } catch (err) {
            console.error('Error refreshing user data:', err);
          }
          
          // Load notifications from API
          try {
            const notifData = await getNotifications();
            if (notifData.notifications) {
              const mapped = notifData.notifications.map((n: any) => ({
                id: String(n.id),
                userId: n.userId,
                type: n.type,
                title: n.title,
                message: n.message,
                link: n.link,
                read: !!n.isRead,
                createdAt: n.createdAt ? new Date(n.createdAt * 1000).toISOString() : new Date().toISOString(),
              }));
              setNotifications(mapped);
            }
          } catch (err) {
            console.error('Error loading notifications:', err);
          }
          
          // Load user's favorites from API
          try {
            const favResponse = await getFavorites();
            if (favResponse.success && favResponse.favorites) {
              const favoriteIds = favResponse.favorites.map((fav: any) => String(fav.id || fav.property_id));
              setFavorites(favoriteIds);
            }
          } catch (err) {
            console.error('Error loading favorites:', err);
          }
        }
      } else {
        // Sesija je istekla, očisti podatke
        sessionUtils.clearSession();
      }
      setChecking(false);
    }
    restoreSession();
    
    // Load properties from API
    loadPropertiesFromApi();
  }, []);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  return (
    <ToastProvider>
      <BrowserRouter>
        <ScrollToTop />
        <SPARedirectHandler />
        <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-900 transition-colors">
          <Header />
          <main className="flex-1">
            <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/ponuda" element={<OffersPage />} />
            <Route path="/ponuda/:id" element={<PropertyDetailPage />} />
            <Route path="/potraznja" element={<DemandsPage />} />
            <Route path="/potraznja/:id" element={<PropertyDetailPage />} />
            <Route path="/aukcije" element={<AuctionsPage />} />
            <Route path="/aukcije/:id" element={<PropertyDetailPage />} />
            <Route path="/oglas/:id" element={<PropertyDetailPage />} />
            <Route path="/prijava" element={<LoginPage />} />
            <Route path="/registracija" element={<RegisterPage />} />
            <Route path="/zaboravljena-lozinka" element={<ForgotPasswordPage />} />
            <Route path="/reset-lozinke" element={<ResetPasswordPage />} />
            <Route path="/verifikacija-emaila" element={<VerifyEmailPage />} />
            <Route path="/odjava-newsletter" element={<NewsletterUnsubscribePage />} />
            <Route path="/novi-oglas" element={<NewListingPage />} />
            <Route path="/izmeni-oglas/:id" element={<EditListingPage />} />
            <Route path="/moji-oglasi" element={<MyListingsPage />} />
            <Route path="/profil" element={<ProfilePage />} />
            <Route path="/kupi-tokene" element={<BuyTokensPage />} />
            <Route path="/placanje-uspesno" element={<PaymentSuccessPage />} />
            <Route path="/placanje-neuspesno" element={<PaymentFailedPage />} />
            <Route path="/uslovi-koriscenja" element={<TermsPage />} />
            <Route path="/politika-privatnosti" element={<PrivacyPage />} />
            <Route path="/cenovnik" element={<PricingPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/cesta-pitanja" element={<FAQPage />} />
            <Route path="/o-nama" element={<AboutPage />} />
            <Route path="/mapa" element={<MapPage />} />
            <Route path="/omiljeni" element={<FavoritesPage />} />
            <Route path="/notifikacije" element={<NotificationsPage />} />
            <Route path="/moje-pretrage" element={<SavedSearchesPage />} />
            <Route path="/kalendar" element={<CalendarPage />} />
            <Route path="/kompanija/:username" element={<CompanyProfilePage />} />
            <Route path="/kompanija" element={<CompaniesListPage />} />
            <Route path="/company/:username" element={<CompanyProfilePage />} />
            <Route path="/company" element={<CompaniesListPage />} />
          </Routes>
        </main>
        <Footer />
        <AIAssistant />
      </div>
    </BrowserRouter>
    </ToastProvider>
  );
}

export default App;
